import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
  UseQueryResult,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Helper function to replace path params like :projectId, :kernelId, etc.
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key)
  );
}

export type ApiCheckpointDetail = {
  id: string;
  title: string;
  description: string;
  checkpointNumber: number;
  dueDate: string;
  weightPercentage: number;
  isRequired: boolean;
  status: "completed" | "in_progress" | "not_started" | "overdue";
  startedAt?: string | null;
  submittedAt?: string | null;
  completedAt?: string | null;
  instructorFeedback?: string | null;
  timeSpentMinutes?: number;
  submittedBy?: string;
  maxScore?: string | number;
  totalScore?: string | number;
  feedback?: string;
};

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export type AllGradesResponse = {
  isSuccess: boolean;
  message: string;
  data: data;
};

export type data = {
  grades: ApiGradeItem[];
  pagination: Pagination;
};

export type ApiGradeItem = {
  id: string;
  totalScore: string;
  maxScore: string;
  percentage: string;
  feedback: string;
  checkpoints: {
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
    overdue: number;
    details: ApiCheckpointDetail[];
  };
  projectStatus?: string; // Added to capture project status from API response
  gradedAt?: string;
  submission: {
    id: string;
    submittedAt: string;
    status: "graded" | "grading" | "submitted" | "revision_needed";
    user: {
      id: string;
      name: string;
      email: string;
      profilePicture?: string;
    };
  };
  project: {
    id: string;
    title: string;
    dueDate: string;
    course: { id: string; name: string; code: string };
    isGroup?: string;
    type?: string;
  };
  evaluator?: { id: string; name: string; email: string };
};

// --- Local "Project" type expected by the table/GradeDetailsPage ---
export interface Checkpoint {
  id: string;
  title: string;
  description: string;
  status: "completed" | "in_progress" | "submitted" | "graded" | "overdue";
  dueDate: string;
  submittedDate?: string;
  grade?: string;
  instructorFeedback?: string[];
  submittedBy?: string;
  maxScore?: string | number;
  totalScore?: string | number;
}
interface Submission {
  id: string;
  submittedAt: string;
  files: string[];
  grade?: number;
  feedback?: string;
  status: "submitted" | "graded" | "revision_needed";
}
export interface RubricItem {
  id: string;
  criteria: string;
  description: string;
  points: number;
  earnedPoints?: number;
}
export interface Project {
  id: string;
  trimester: number;
  title: string;
  description: string;
  isGroup?: string;
  projectType?: string;
  subject: string;
  instructor: string;
  startDate: string | null;
  dueDate: string;
  createdDate: string | null;
  status: "not_started" | "in_progress" | "submitted" | "graded";
  progress: number;
  totalPoints: number;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  objectives: string[];
  requirements: string[];
  datasets: any[];
  submissions: Submission[];
  rubric: RubricItem[];
  estimatedHours: number;
  tags: string[];
  checkpoints: Checkpoint[];

  _overallScore?: number;
  _maxScore?: number;
  _completedCheckpoints?: number;
  _totalCheckpoints?: number;
  projectStatus?: string; // Added to capture the project status from API
}

// Gradebook API Types
export type StudentGradebookItem = {
  user_id: string;
  name: string;
  email: string;
  assigned_projects_count: number;
};

export type StudentsGradebookResponse = {
  isSuccess: boolean;
  message: string;
  data: {
    data: StudentGradebookItem[];
    pagination: Pagination;
  };
};

export type RubricScore = {
  score: number;
  maxPoints: number;
  criterionName: string;
};

export type Rubric = {
  rubricTitle: string;
  criteriaScores: RubricScore[];
  totalScore: number;
  maxScore: number;
};

export type CheckpointDetail = {
  checkpoint_id: string;
  title: string;
  progress: "completed" | "in_progress" | "not_started";
  rubrics: Rubric[];
  attempts?: number;
  instructor_feedback?: string;
  time_spent_minutes?: number;
  started_at?: string | null;
  submitted_at?: string | null;
  completed_at?: string | null;
  totalScore?: number;
  maxScore?: number;
};

export type StudentProject = {
  project_id: string;
  project_name: string;
  total_checkpoints: number;
  completed_checkpoints: number;
  total_score: number;
  max_score: number;
  evaluator: string;
  course: string;
  due_date: string;
  difficulty_level: "beginner" | "intermediate" | "advanced";
  estimated_hours: number;
  published_at?: string | null;
  latest_submitted_at?: string | null;
  latest_started_at?: string | null;
  checkpoints: CheckpointDetail[];
};

export type StudentDetailedGradebookResponse = {
  isSuccess: boolean;
  message: string;
  data: {
    data: {
      user: {
        id: string;
        name: string;
        email: string;
        status: string;
      };
      projects: StudentProject[];
    };
    pagination: Pagination;
  };
};

export type GradeContext = {
  id: string;
  name: string;
  code: string;
};

export type GradeProject = {
  id: string;
  title: string;
};

export interface GetAllGradesParams {
  isExport?: boolean;
  courseId?: string;
  projectId?: string;
  searchTerm?: string;
  page?: string;
  limit?: string;
}

export async function getAllGrades(
  params: GetAllGradesParams = {}
): Promise<AllGradesResponse> {
  const {
    isExport = false,
    courseId,
    projectId,
    searchTerm,
    page,
    limit,
  } = params;

  const baseUrl = getBITSEndApiMap("getAllGrades").replace("/:isExport", "");
  const queryParams = new URLSearchParams();

  if (isExport) queryParams.append("isExport", "true");
  if (courseId) queryParams.append("courseId", courseId);
  if (projectId) queryParams.append("projectId", projectId);
  if (searchTerm) queryParams.append("search", searchTerm);
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);

  const url = queryParams.toString()
    ? `${baseUrl}?${queryParams.toString()}`
    : baseUrl;
  if (!isExport) {
    const { data } = await api.get<AllGradesResponse>(url);
    return data;
  } else {
    const response = await api.get<AllGradesResponse>(url, {
      responseType: "blob",
    });
    return response.data;
  }
}

export async function getStudentsGradebook(params?: {
  page?: number;
  limit?: number;
  course?: string;
  project?: string;
  search?: string;
}): Promise<StudentsGradebookResponse> {
  const url = getBITSEndApiMap("getStudentsGradebook");
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.course) queryParams.append("course", params.course);
  if (params?.project) queryParams.append("project", params.project);
  if (params?.search) queryParams.append("search", params.search);

  const fullUrl = queryParams.toString()
    ? `${url}?${queryParams.toString()}`
    : url;
  const { data } = await api.get<StudentsGradebookResponse>(fullUrl);
  return data;
}

export async function getStudentDetailedGradebook(
  userId: string,
  params?: {
    page?: number;
    limit?: number;
    course?: string;
    project?: string;
  }
): Promise<StudentDetailedGradebookResponse> {
  const urlTemplate = getBITSEndApiMap("getStudentDetailedGradebook");
  const url = buildUrl(urlTemplate, { user_id: userId });
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.course) queryParams.append("course", params.course);
  if (params?.project) queryParams.append("project", params.project);

  const fullUrl = queryParams.toString()
    ? `${url}?${queryParams.toString()}`
    : url;
  const { data } = await api.get<StudentDetailedGradebookResponse>(fullUrl);
  return data;
}

export async function getGradeContexts(): Promise<{
  isSuccess: boolean;
  message: string;
  data: GradeContext[];
}> {
  const url = getBITSEndApiMap("getGradeContexts");
  const { data } = await api.get(url);
  return data;
}

export async function getCoursesForProjectCreation(): Promise<{
  isSuccess: boolean;
  message: string;
  data: GradeContext[];
}> {
  const url = getBITSEndApiMap("getCourses");
  const { data } = await api.get(url);
  return data;
}

export async function getGradeProjects(params?: {
  status?: string;
  course?: string;
}): Promise<{
  isSuccess: boolean;
  message: string;
  data: GradeProject[];
}> {
  const url = getBITSEndApiMap("getGradeProjects");
  const queryParams = new URLSearchParams();

  if (params?.status) queryParams.append("status", params.status);
  if (params?.course) queryParams.append("courseId", params.course);

  const fullUrl = queryParams.toString()
    ? `${url}?${queryParams.toString()}`
    : url;
  const { data } = await api.get(fullUrl);
  return data;
}
